import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar, Plus, Edit, Trash2, Power, PowerOff, RefreshCw } from 'lucide-react';
import Layout from '@/components/Layout';
import AddPeriodModal from '@/components/AddPeriodModal';
import EditPeriodModal from '@/components/EditPeriodModal';
import BatchStatusUpdateModal from '@/components/BatchStatusUpdateModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
});

const PeriodsPage = ({ user, onLogout }) => {
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [showBatchStatusModal, setShowBatchStatusModal] = useState(false);
  const [selectedPeriodForBatch, setSelectedPeriodForBatch] = useState(null);

  useEffect(() => {
    fetchPeriods();
  }, []);

  const fetchPeriods = async () => {
    try {
      const response = await axios.get(`${API}/periods`, getAuthHeaders());
      setPeriods(response.data);
    } catch (error) {
      toast.error('Erro ao carregar períodos');
    } finally {
      setLoading(false);
    }
  };

  const handleDeletePeriod = async (periodId) => {
    if (!window.confirm('Tem certeza que deseja excluir este período?')) return;
    try {
      await axios.delete(`${API}/periods/${periodId}`, getAuthHeaders());
      toast.success('Período excluído com sucesso');
      fetchPeriods();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir período');
    }
  };

  const handleTogglePeriodStatus = async (period) => {
    const newStatus = period.status === 'ativo' ? 'inativo' : 'ativo';
    try {
      await axios.put(`${API}/periods/${period.id}`, { status: newStatus }, getAuthHeaders());
      toast.success(`Período ${newStatus === 'ativo' ? 'ativado' : 'desativado'} com sucesso`);
      fetchPeriods();
    } catch (error) {
      toast.error('Erro ao atualizar status do período');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('pt-BR');
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
              <Calendar className="h-8 w-8 text-primary" />
              Períodos de Cadastro
            </h1>
            <p className="text-muted-foreground mt-1">
              {periods.length} período(s) cadastrado(s)
            </p>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Novo Período
          </Button>
        </div>

        {/* Periods List */}
        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : periods.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              Nenhum período cadastrado
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {periods.map((period) => (
              <Card 
                key={period.id} 
                className={`hover:shadow-md transition-shadow ${period.status === 'inativo' ? 'opacity-60' : ''}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-primary" />
                      <h3 className="font-semibold text-lg">{period.name}</h3>
                    </div>
                    <Badge variant={period.status === 'ativo' ? 'default' : 'secondary'}>
                      {period.status === 'ativo' ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                  
                  <div className="space-y-2 text-sm text-muted-foreground mb-4">
                    <p><strong>Início:</strong> {formatDate(period.start_date)}</p>
                    <p><strong>Fim:</strong> {formatDate(period.end_date)}</p>
                    {period.client_count !== undefined && (
                      <p><strong>Clientes:</strong> {period.client_count}</p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setEditingPeriod(period); setShowEditModal(true); }}
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => handleTogglePeriodStatus(period)}
                    >
                      {period.status === 'ativo' ? (
                        <><PowerOff className="w-4 h-4 mr-1" />Desativar</>
                      ) : (
                        <><Power className="w-4 h-4 mr-1" />Ativar</>
                      )}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => { setSelectedPeriodForBatch(period); setShowBatchStatusModal(true); }}
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      Status em Lote
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-destructive hover:bg-destructive/10"
                      onClick={() => handleDeletePeriod(period.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Modals */}
        {showAddModal && (
          <AddPeriodModal
            onClose={() => setShowAddModal(false)}
            onSuccess={() => { setShowAddModal(false); fetchPeriods(); }}
          />
        )}
        {showEditModal && editingPeriod && (
          <EditPeriodModal
            period={editingPeriod}
            onClose={() => { setShowEditModal(false); setEditingPeriod(null); }}
            onSuccess={() => { setShowEditModal(false); setEditingPeriod(null); fetchPeriods(); }}
          />
        )}
        {showBatchStatusModal && selectedPeriodForBatch && (
          <BatchStatusUpdateModal
            period={selectedPeriodForBatch}
            onClose={() => { setShowBatchStatusModal(false); setSelectedPeriodForBatch(null); }}
            onSuccess={() => { setShowBatchStatusModal(false); setSelectedPeriodForBatch(null); fetchPeriods(); }}
          />
        )}
      </div>
    </Layout>
  );
};

export default PeriodsPage;
